import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import { handleSessionExpired, isSessionExpiredError } from './lib/session';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ScrollToTop } from './components/ScrollToTop';
import { Dashboard } from './pages/Dashboard';
import { Identities } from './pages/Identities';
import { Contexts } from './pages/Contexts';
import { BulkOperations } from './pages/BulkOperations';
import { DataRequests } from './pages/DataRequests';
import { JobHistory } from './pages/JobHistory';
import { JobDetails } from './pages/JobDetails';
import { Settings } from './pages/Settings';
import { Plans } from './pages/Plans';
import { Help } from './pages/Help';
import { Login } from './pages/Login';
import { CheckoutInit } from './pages/CheckoutInit';
import { CheckoutSuccess } from './pages/CheckoutSuccess';

const queryClient = new QueryClient({
  // A session that expires mid-navigation previously only logged the user
  // out if the /me query happened to be the one that hit the 401 — any
  // other query just failed silently, rendering as missing/empty data.
  // Catching it here means every query/mutation's 401 triggers logout.
  queryCache: new QueryCache({
    onError: (error) => {
      if (isSessionExpiredError(error)) handleSessionExpired();
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isSessionExpiredError(error)) handleSessionExpired();
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => !isSessionExpiredError(error) && failureCount < 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/identities"
            element={
              <ProtectedRoute>
                <Layout>
                  <Identities />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/identities/add-alias"
            element={
              <ProtectedRoute>
                <Layout>
                  <Identities />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/identities/search"
            element={
              <ProtectedRoute>
                <Layout>
                  <Identities />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/contexts"
            element={
              <ProtectedRoute>
                <Layout>
                  <Contexts />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk"
            element={
              <ProtectedRoute>
                <Layout>
                  <BulkOperations />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk/replace"
            element={
              <ProtectedRoute>
                <Layout>
                  <BulkOperations />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk/json"
            element={
              <ProtectedRoute>
                <Layout>
                  <BulkOperations />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk/jobs"
            element={
              <ProtectedRoute>
                <Layout>
                  <JobHistory />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/bulk/jobs/:jobId"
            element={
              <ProtectedRoute>
                <Layout>
                  <JobDetails />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dsr"
            element={
              <ProtectedRoute>
                <Layout>
                  <DataRequests />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/dsr/history"
            element={
              <ProtectedRoute>
                <Layout>
                  <DataRequests />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <Settings />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/billing/plans"
            element={
              <ProtectedRoute>
                <Layout>
                  <Plans />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <Layout>
                  <Help />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <CheckoutInit />
              </ProtectedRoute>
            }
          />

          <Route
            path="/checkout/success"
            element={
              <ProtectedRoute>
                <CheckoutSuccess />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
